import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { apiFetch } from '../utils/api'
import { validatePasswordComplexity } from '../utils/validation'
import AddressFields from './AddressFields'

const UserForm = ({ user, onSave, onCancel, isEditing = false, currentUserId = null }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    city: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [isActive, setIsActive] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  // Fill form with user data when editing
  useEffect(() => {
    if (isEditing && user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        street: user.street || '',
        postalCode: user.postal_code || '',
        city: user.city || '',
        email: user.email || '',
        password: '',
        confirmPassword: ''
      })
      setIsActive(user.is_active !== false)
    }
  }, [isEditing, user])

  // Admins cannot lock their own account
  const canToggleActive = isEditing && user && user.id !== currentUserId

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const validatePasswordForNewUser = () => {
    if (!formData.password) {
      return 'Password is required for new users'
    }
    const passwordError = validatePasswordComplexity(formData.password)
    if (passwordError) {
      return passwordError
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match'
    }
    return null
  }

  const prepareSubmitData = () => {
    const baseData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      street: formData.street,
      postalCode: formData.postalCode,
      city: formData.city,
      email: formData.email
    }
    
    if (!isEditing) return { ...baseData, password: formData.password }
    return canToggleActive ? { ...baseData, isActive } : baseData
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Validate passwords for new users
    if (!isEditing) {
      const validationError = validatePasswordForNewUser()
      if (validationError) {
        setError(validationError)
        setIsLoading(false)
        return
      }
    }

    try {
      const response = await apiFetch(
        isEditing
          ? `/api/admin/users/${user.id}`
          : `/api/admin/users`,
        { method: isEditing ? 'PUT' : 'POST', body: JSON.stringify(prepareSubmitData()) }
      )
      const data = await response.json()

      if (response.ok) {
        onSave(data.user)
      } else {
        setError(data.error || `Failed to ${isEditing ? 'update' : 'create'} user`)
      }
    } catch (err) {
      setError('Network error. Please check if the backend server is running.')
      console.error(`${isEditing ? 'Update' : 'Create'} user error:`, err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="user-form-overlay">
      <div className="user-form-container">
        <div className="user-form-header">
          <h3>{isEditing ? 'Edit User' : 'Add New User'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form className="user-form" onSubmit={handleSubmit}>
          <AddressFields formData={formData} onChange={handleChange} isLoading={isLoading} />

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
              required
              disabled={isLoading}
            />
          </div>

          {canToggleActive && (
            <div className="form-group">
              <label htmlFor="isActive">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={isLoading}
                />
                {' '}Konto aktiv
              </label>
            </div>
          )}

          {!isEditing && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  required
                  disabled={isLoading}
                  minLength="12"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  required
                  disabled={isLoading}
                  minLength="12"
                />
              </div>
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-save"
              disabled={isLoading}
            >
              {isLoading && <span className="loading-spinner"></span>}
              {(() => {
                if (isLoading) {
                  return isEditing ? 'Updating...' : 'Creating...'
                }
                return isEditing ? 'Update' : 'Save'
              })()}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

UserForm.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    street: PropTypes.string,
    postal_code: PropTypes.string,
    city: PropTypes.string,
    email: PropTypes.string,
    is_active: PropTypes.bool,
  }),
  currentUserId: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
}

export default UserForm 